import React from 'react';
import { ADMIN_MESSAGE_FORMATS } from '../../../../constants/messageFormats';
import ChatMessageContent from './ChatMessageContent';
import ChatImageGroupBubble from '../composer/ChatImageGroupBubble';
import { renderMentionTextNodes } from '../mentions/chatMentionUtils';
import { getFileVisual } from '../composer/fileIconClass';
import { ellipsizeFileName } from '../composer/fileNameUtils';
import {
  hasMissingChatFileStorage,
  hasMissingChatImageStorage,
  resolveChatFileUnavailableText,
  resolveChatImageUnavailableText,
} from '../composer/chatAttachmentAvailability';

export function resolveIndexedImagePreview({
  message,
  imageIndexByMessageId,
  imageMessages,
  imagePreviewUrls,
  buildPreviewUrl,
}) {
  const imageIndex = imageIndexByMessageId.get(message.id);
  const fallbackIndex =
    imageIndex ?? imageMessages.findIndex((item) => item.image_file_name === message.image_file_name);
  const safeIndex = fallbackIndex != null && fallbackIndex >= 0 ? fallbackIndex : -1;
  const previewSrc =
    (safeIndex >= 0 ? imagePreviewUrls[safeIndex] : null) ||
    (typeof buildPreviewUrl === 'function' ? buildPreviewUrl(message.image_file_name) : '');

  return {
    safeIndex,
    previewSrc,
  };
}

const renderLinkedText = (source = '') => {
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  return String(source || '').split('\n').map((line, lineIndex) => (
    <React.Fragment key={`line-${lineIndex}`}>
      {line.split(urlPattern).map((part, partIndex) =>
        /^https?:\/\//.test(part) ? (
          <a key={`url-${lineIndex}-${partIndex}`} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </a>
        ) : (
          part
        )
      )}
      <br />
    </React.Fragment>
  ));
};

const renderTextMessageContent = (message = {}, onMentionClick = null) => (
  <ChatMessageContent
    format="text"
    text={message.content || ''}
    mentions={Array.isArray(message.mentions) ? message.mentions : []}
    textNode={
      <span>
        {renderMentionTextNodes({
          text: message.content || '',
          mentions: Array.isArray(message.mentions) ? message.mentions : [],
          linkifyText: renderLinkedText,
          onMentionClick,
        })}
      </span>
    }
  />
);

export function renderIndividualStandardMessageContent({
  message,
  replyContent = null,
  formatMessageContent,
  formatExpireDateLabel,
  fileName,
  fileUrl = '',
  canDownloadFile,
  onDownloadFile,
  imagePreviewSrc,
  onOpenImage,
  onDownloadImage,
  imageGroupItems = [],
  buildImageGroupPreviewItem = null,
  onOpenImageGroupItem = null,
  imageUnavailableText = '画像の保存期間は1週間です。',
  fileUnavailableText = 'ファイルの保存期間は1週間です。',
  onMentionClick = null,
}) {
  const safeMessage = message || {};
  const hasMissingImageStorage = hasMissingChatImageStorage(safeMessage);
  const hasMissingFileStorage = hasMissingChatFileStorage(safeMessage);

  if (safeMessage.message_format === ADMIN_MESSAGE_FORMATS.FILE) {
    const resolvedFileName = String(fileName || '');
    const fileVisual = getFileVisual(resolvedFileName);
    return (
      <>
        {replyContent}
        <ChatMessageContent
          format="file"
          fileVariant="rich"
          fileName={ellipsizeFileName(resolvedFileName)}
          fileUrl={fileUrl}
          fileCanDownload={hasMissingFileStorage ? false : canDownloadFile}
          onFileAction={canDownloadFile && !hasMissingFileStorage ? onDownloadFile : null}
          fileIconClass={fileVisual.iconClass}
          fileVisualKind={fileVisual.kind}
          fileMeta={
            safeMessage.file_expires_at
              ? `保存期間：${formatExpireDateLabel?.(safeMessage.file_expires_at)}まで`
              : ''
          }
          fileDisabledText={resolveChatFileUnavailableText(safeMessage, fileUnavailableText)}
        />
      </>
    );
  }

  if (safeMessage.message_format === ADMIN_MESSAGE_FORMATS.IMAGE) {
    const groupedImageItems = Array.isArray(imageGroupItems) && imageGroupItems.length > 1
      ? imageGroupItems
          .map((item, index) =>
            typeof buildImageGroupPreviewItem === 'function'
              ? buildImageGroupPreviewItem(item, index)
              : null
          )
          .filter(Boolean)
      : [];
    if (groupedImageItems.length > 1) {
      return (
        <>
          {replyContent}
          <ChatImageGroupBubble
            items={groupedImageItems}
            onOpen={(item, index) => {
              if (typeof onOpenImageGroupItem === 'function') {
                onOpenImageGroupItem(item, index);
              }
            }}
            unavailableText={resolveChatImageUnavailableText(safeMessage, imageUnavailableText)}
          />
          {safeMessage.image_expires_at ? (
            <div className="download-row chat-message-content__downloadRow">
              <span className="expire-note-inline chat-message-content__metaText">
                保存期間：{formatExpireDateLabel?.(safeMessage.image_expires_at)}まで
              </span>
            </div>
          ) : null}
        </>
      );
    }

    return (
      <>
        {replyContent}
        <ChatMessageContent
          format="image"
          imageSrc={safeMessage.image_file_name && !hasMissingImageStorage ? imagePreviewSrc : ''}
          imageAlt="メッセージ画像"
          imageWidth={safeMessage.image_width}
          imageHeight={safeMessage.image_height}
          onImageClick={safeMessage.image_file_name && !hasMissingImageStorage ? onOpenImage : null}
          onImageDownload={safeMessage.image_file_name && !hasMissingImageStorage ? onDownloadImage : null}
          imageMeta={
            safeMessage.image_expires_at
              ? `保存期間：${formatExpireDateLabel?.(safeMessage.image_expires_at)}まで`
              : ''
          }
          imageUnavailableText={resolveChatImageUnavailableText(safeMessage, imageUnavailableText)}
        />
      </>
    );
  }

  return (
    <div>
      {replyContent}
      {Array.isArray(safeMessage.mentions) && safeMessage.mentions.length > 0
        ? renderTextMessageContent(safeMessage, onMentionClick)
        : formatMessageContent?.(safeMessage.content)}
    </div>
  );
}
